import { RowCrudStatus } from "../../common/types";
import { EditionDocumentation } from "../../../api/graphql";
import { LangStore } from "../../../store";
import { musicalWorkDisplayId } from "../../musicalWork/store/MusicalWorkCommonStore";
import { territoryDisplayId } from "../../territory/store/TerritoryCommonStore";
import { authorComposerDisplayId } from "../../authorComposer/store/AuthorComposerCommonStore";
import {
  EditionContractPublisherValuesType,
  getEditionContractPublisherValues,
} from "../../editionContractPublisher/store/EditionContractPublisherCommonStore";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";

let uniqueCounter = 1;
export type EditionDocumentationValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  __works: string[];
  showWorks: { displayId: string; id: string }[];
  __territories: string[];
  showTerritories: { displayId: string; id: string }[];
  __authors: string[];
  showAuthors: { displayId: string; id: string }[];
  authorsDisplay: string;
  showPublishers: EditionContractPublisherValuesType[];
  publisherConfiguration: string;
  publisherConfigurationDisplay: string;
  territoriesDisplay: string;
  contractDuration: number | undefined;
  contractRenewal: number | undefined;
  contractNumber: string;
  documentName: string;
  finalDocumentPdf: string;
  finalDocumentPdf_data: FileCRUDType;
  __finalDocumentPdf_files: FileCRUDType[];
};

export type EditionDocumentationErrorsType = {
  works?: { message: string; fieldLabel: string };
  territories?: { message: string; fieldLabel: string };
  authors?: { message: string; fieldLabel: string };
  authorsDisplay?: { message: string; fieldLabel: string };
  publishers?: { message: string; fieldLabel: string };
  publisherConfiguration?: { message: string; fieldLabel: string };
  publisherConfigurationDisplay?: { message: string; fieldLabel: string };
  territoriesDisplay?: { message: string; fieldLabel: string };
  contractDuration?: { message: string; fieldLabel: string };
  contractRenewal?: { message: string; fieldLabel: string };
  contractNumber?: { message: string; fieldLabel: string };
  documentName?: { message: string; fieldLabel: string };
  finalDocumentPdf?: { message: string; fieldLabel: string };
};

export function EditionDocumentationValuesInit(): EditionDocumentationValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    __works: [],
    showWorks: [],
    __territories: [],
    showTerritories: [],
    __authors: [],
    showAuthors: [],
    authorsDisplay: "",
    showPublishers: [],
    publisherConfiguration: "",
    publisherConfigurationDisplay: "",
    territoriesDisplay: "",
    contractDuration: undefined,
    contractRenewal: undefined,
    contractNumber: "",
    documentName: "",
    finalDocumentPdf: "",
    finalDocumentPdf_data: fileCRUDInit(),
    __finalDocumentPdf_files: [],
  };
}

export type EditionDocumentationType =
  | EditionDocumentation
  | EditionDocumentationValuesType;

export function editionDocumentationDisplayId(
  source: Partial<EditionDocumentation>,
  _langStore: LangStore
) {
  return ((): string => {
    const __p = (source.documentName ?? "").trim();
    return __p ? __p : source.contractNumber ?? _langStore.emptyStringSymbol;
  })();
}

export function getEditionDocumentationValues(
  source: EditionDocumentationType,
  _langStore: LangStore,
  _duplicated: boolean = false
): EditionDocumentationValuesType {
  const entityValues = EditionDocumentationValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : editionDocumentationDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.showWorks =
    "works" in source && source.works
      ? (source.works || []).map((x) => {
          return { id: x.id, displayId: musicalWorkDisplayId(x, _langStore) };
        })
      : "showWorks" in source && source.showWorks
      ? (source.showWorks || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__works = entityValues.showWorks.map((x) => x.displayId);
  entityValues.showTerritories =
    "territories" in source && source.territories
      ? (source.territories || []).map((x) => {
          return { id: x.id, displayId: territoryDisplayId(x, _langStore) };
        })
      : "showTerritories" in source && source.showTerritories
      ? (source.showTerritories || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__territories = entityValues.showTerritories.map(
    (x) => x.displayId
  );
  entityValues.showAuthors =
    "authors" in source && source.authors
      ? (source.authors || []).map((x) => {
          return {
            id: x.id,
            displayId: authorComposerDisplayId(x, _langStore),
          };
        })
      : "showAuthors" in source && source.showAuthors
      ? (source.showAuthors || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__authors = entityValues.showAuthors.map((x) => x.displayId);
  entityValues.authorsDisplay = source.authorsDisplay || "";
  entityValues.showPublishers =
    "publishers" in source && source.publishers
      ? (source.publishers || []).map((x) => {
          const row: EditionContractPublisherValuesType =
            getEditionContractPublisherValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showPublishers" in source && source.showPublishers
      ? (source.showPublishers || []).map((x) => {
          const row: EditionContractPublisherValuesType =
            getEditionContractPublisherValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.publisherConfiguration = source.publisherConfiguration || "";
  entityValues.publisherConfigurationDisplay =
    source.publisherConfigurationDisplay || "";
  entityValues.territoriesDisplay = source.territoriesDisplay || "";
  entityValues.contractDuration =
    (source.contractDuration as unknown) === "" ||
    source.contractDuration === undefined ||
    source.contractDuration === null
      ? undefined
      : source.contractDuration;
  entityValues.contractRenewal =
    (source.contractRenewal as unknown) === "" ||
    source.contractRenewal === undefined ||
    source.contractRenewal === null
      ? undefined
      : source.contractRenewal;
  entityValues.contractNumber = source.contractNumber;
  entityValues.documentName = source.documentName || "";
  entityValues.__finalDocumentPdf_files =
    source.finalDocumentPdf_data && !_duplicated
      ? [
          {
            id:
              "id" in source.finalDocumentPdf_data &&
              source.finalDocumentPdf_data.id
                ? source.finalDocumentPdf_data.id
                : `generated_${uniqueCounter++}`,
            name: source.finalDocumentPdf_data.name,
            description: source.finalDocumentPdf_data.description || undefined,
            size: source.finalDocumentPdf_data.size || 0,
            formattedSize: source.finalDocumentPdf_data.size
              ? formatBytes(source.finalDocumentPdf_data.size)
              : "",
            mimetype: source.finalDocumentPdf_data.mimetype,
            url: source.finalDocumentPdf_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.finalDocumentPdf =
    source.finalDocumentPdf && !_duplicated ? source.finalDocumentPdf : "";
  entityValues.finalDocumentPdf_data =
    source.finalDocumentPdf_data && "__status__" in source.finalDocumentPdf_data
      ? source.finalDocumentPdf_data
      : source.finalDocumentPdf_data
      ? {
          id: ("id" in source.finalDocumentPdf_data &&
          source.finalDocumentPdf_data.id
            ? source.finalDocumentPdf_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.finalDocumentPdf_data.name,
          description: source.finalDocumentPdf_data.description || undefined,
          size: source.finalDocumentPdf_data.size || 0,
          formattedSize: source.finalDocumentPdf_data.size
            ? formatBytes(source.finalDocumentPdf_data.size)
            : "",
          mimetype: source.finalDocumentPdf_data.mimetype,
          url: source.finalDocumentPdf_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}
